import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Target,
  GraduationCap,
  FileText,
  Plane,
  Award,
  DollarSign,
  Home,
  Briefcase,
} from 'lucide-react';
import { motion } from 'framer-motion';

// Service illustration icons from assets
import iconProfileAssessment from '../../assets/Icon/Profile Assessment.png';
import iconPreapplication from '../../assets/Icon/Preapplication Support.png';
import iconApplyUniversity from '../../assets/Icon/Apply University.png';
import iconVisaInterview from '../../assets/Icon/Visa & Interview Support.png';
import iconTicketTravel from '../../assets/Icon/Ticket & Travel Support.png';
import iconAccommodation from '../../assets/Icon/Find Accommodation.png';
import iconEducationLoan from '../../assets/Icon/loan1.png';
import iconFindJobs from '../../assets/Icon/Find Jobs Abroad.png';
import iconSuccessful from '../../assets/Icon/sucessful.png';

// Service types configuration
const SERVICE_CONFIG = {
  PROFILE_ASSESSMENT: {
    name: 'Profile Assessment',
    icon: iconProfileAssessment,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
  },
  UNIVERSITY_SHORTLISTING: {
    name: 'Preapplication Support',
    icon: iconPreapplication,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
  },
  APPLICATION_ASSISTANCE: {
    name: 'Apply University',
    icon: iconApplyUniversity,
    color: 'from-pink-500 to-pink-600',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-700',
  },
  VISA_GUIDANCE: {
    name: 'Visa & Interview Support',
    icon: iconVisaInterview,
    color: 'from-orange-500 to-orange-600',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
  },
  SCHOLARSHIP_SEARCH: {
    name: 'Ticket & Travel Support',
    icon: iconTicketTravel,
    color: 'from-amber-500 to-amber-600',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
  },
  LOAN_ASSISTANCE: {
    name: 'Education Loan',
    icon: iconEducationLoan,
    color: 'from-rose-500 to-rose-600',
    bgColor: 'bg-rose-50',
    textColor: 'text-rose-700',
  },
  ACCOMMODATION_HELP: {
    name: 'Find Accommodation',
    icon: iconAccommodation,
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
  },
  PRE_DEPARTURE_ORIENTATION: {
    name: 'Find Jobs Abroad',
    icon: iconFindJobs,
    color: 'from-teal-500 to-teal-600',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-700',
  },
};

export default function ServiceTypeFilterCards({
  requests = [],
  selectedType,
  onSelectType,
}) {
  // Calculate counts per service type
  const serviceCounts = Object.keys(SERVICE_CONFIG).reduce((acc, type) => {
    acc[type] = requests.filter(r => r.serviceType === type).length;
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
      {Object.entries(SERVICE_CONFIG).map(([type, config], index) => {
        const Icon = config.icon;
        const count = serviceCounts[type] || 0;
        const isSelected = selectedType === type;

        return (
          <motion.div
            key={type}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card
              className={`p-3 cursor-pointer transition-all hover:shadow-md ${
                isSelected
                  ? 'ring-2 ring-primary shadow-md bg-primary/5'
                  : 'hover:bg-primary/10'
              }`}
              onClick={() => onSelectType(isSelected ? null : type)}
            >
              <div className="flex flex-col items-center text-center gap-2">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 shadow-[0_4px_12px_0_rgba(28,141,238,0.12)] flex items-center justify-center group-hover:shadow-[0_6px_20px_0_rgba(28,141,238,0.20)] group-hover:-translate-y-1 transition-all duration-200">
                  <img
                    src={config.icon}
                    alt={config.name}
                    className="w-9 h-9 object-contain group-hover:scale-105 transition-transform duration-200"
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-700 line-clamp-1">
                    {config.name}
                  </p>
                  <Badge
                    className={`mt-1 ${config.bgColor} ${config.textColor} border-0 text-xs`}
                  >
                    {count}
                  </Badge>
                </div>
              </div>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
}

// Export SERVICE_CONFIG for use in other components
export { SERVICE_CONFIG };
